import React, { useState, useEffect, lazy, Suspense, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';

// Critical components loaded immediately
import Navigation from './components/Navigation';
import HeroSection from './components/HeroSection';

// Lazy load non-critical components for better performance
const ProcessSection = lazy(() => import('./components/ProcessSection'));
const PricingSection = lazy(() => import('./components/PricingSection'));
const FeaturesSection = lazy(() => import('./components/FeaturesSection'));
const CompaniesSection = lazy(() => import('./components/CompaniesSection'));
const TestimonialsSection = lazy(() => import('./components/TestimonialsSection'));
const AboutSection = lazy(() => import('./components/AboutSection'));
const Footer = lazy(() => import('./components/Footer'));
const WhatsAppFloat = lazy(() => import('./components/WhatsAppFloat'));
const AgreementPage = lazy(() => import('./components/AgreementPage'));

// Loading component for lazy-loaded components
const LoadingFallback = () => (
  <div className="flex items-center justify-center py-8 sm:py-12 lg:py-16">
    <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-b-2 border-purple-600"></div>
  </div>
);

// Main Home Page Component
const HomePage = () => {
  const [activeSection, setActiveSection] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [showFloatingNav, setShowFloatingNav] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Memoized scroll handler to prevent excessive re-renders
  const handleScroll = useCallback(() => {
    const scrollY = window.scrollY;
    setShowFloatingNav(scrollY > 100);
    
    const sections = ['section-0', 'section-1', 'section-2', 'section-3', 'section-4', 'section-5', 'section-6'];
    const scrollPosition = scrollY + window.innerHeight / 2;
    
    sections.forEach((sectionId, index) => {
      const element = document.getElementById(sectionId);
      if (element) {
        const elementTop = element.offsetTop;
        const elementBottom = elementTop + element.offsetHeight;
        
        if (scrollPosition >= elementTop && scrollPosition <= elementBottom) {
          setActiveSection(index);
        }
      }
    });
  }, []);

  // Debounced scroll handler for better performance
  useEffect(() => {
    setIsVisible(true);
    
    let ticking = false;
    const debouncedScrollHandler = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    handleScroll(); // Initial call
    window.addEventListener('scroll', debouncedScrollHandler, { passive: true });
    return () => window.removeEventListener('scroll', debouncedScrollHandler);
  }, [handleScroll]);

  // Auto-playing step effect
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % 4);
    }, 4000);
    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  // Memoized step click handler
  const handleStepClick = useCallback((index) => {
    setActiveStep(index);
    setIsAutoPlaying(false);
    
    setTimeout(() => {
      setIsAutoPlaying(true);
    }, 10000);
  }, []);

  // Memoized scroll to section handler
  const scrollToSection = useCallback((index) => {
    setActiveSection(index);
    const element = document.getElementById(`section-${index}`);
    element?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  return (
    <div className="bg-white">
      <Navigation 
        showFloatingNav={showFloatingNav} 
        activeSection={activeSection} 
        scrollToSection={scrollToSection} 
      />
      
      <HeroSection 
        isVisible={isVisible} 
        scrollToSection={scrollToSection} 
      />
      
      <Suspense fallback={<LoadingFallback />}>
        <ProcessSection 
          activeStep={activeStep}
          setActiveStep={setActiveStep}
          isAutoPlaying={isAutoPlaying}
          handleStepClick={handleStepClick}
        />
      </Suspense>
      
      <Suspense fallback={<LoadingFallback />}>
        <PricingSection />
      </Suspense>
      
      <Suspense fallback={<LoadingFallback />}>
        <FeaturesSection isVisible={isVisible} />
      </Suspense>
      
      <Suspense fallback={<LoadingFallback />}>
        <CompaniesSection />
      </Suspense>
      
      <Suspense fallback={<LoadingFallback />}>
        <TestimonialsSection />
      </Suspense>
      
      <Suspense fallback={<LoadingFallback />}>
        <AboutSection />
      </Suspense>
      
      <Suspense fallback={<LoadingFallback />}>
        <Footer />
      </Suspense>
      
      <Suspense fallback={null}>
        <WhatsAppFloat />
      </Suspense>
    </div>
  );
};

const App = () => {
  return (
    <Router>
      <div className="font-['Inter',sans-serif]">
        
        <Routes>
          {/* Home Page Route */}
          <Route path="/" element={<HomePage />} />
          
          {/* Agreement Page Route */}
          <Route 
            path="/agreement" 
            element={
              <Suspense fallback={<LoadingFallback />}>
                <AgreementPage />
              </Suspense>
            } 
          />
        </Routes>

        {/* Fixed CSS - removed jsx attribute */}
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
          
          /* Mobile-first animations */
          .animate-scroll {
            animation: scroll 40s linear infinite;
          }
          
          @media (min-width: 640px) {
            .animate-scroll {
              animation: scroll 35s linear infinite;
            }
          }
          
          @media (min-width: 1024px) {
            .animate-scroll {
              animation: scroll 30s linear infinite;
            }
          }
          
          @keyframes scroll {
            0% { transform: translateX(0); }
            100% { transform: translateX(-33.333%); }
          }
          
          @keyframes gradientShift {
            0%, 100% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
          }
          
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
          }

          /* Optimize animations for mobile performance */
          @media (prefers-reduced-motion: reduce) {
            .animate-scroll,
            .animate-bounce,
            [style*="animation"] {
              animation-duration: 0.01ms !important;
              animation-iteration-count: 1 !important;
              opacity: 1 !important;
              transform: none !important;
            }
          }

          /* Improve touch targets for mobile */
          @media (max-width: 640px) {
            button,
            [role="button"],
            .clickable {
              min-height: 44px;
              min-width: 44px;
            }
          }

          /* Optimize font loading */
          @font-face {
            font-family: 'Inter';
            font-style: normal;
            font-weight: 400;
            font-display: swap;
          }

          /* Prevent horizontal scroll on mobile */
          body {
            overflow-x: hidden;
          }

          /* Smooth scrolling for iOS */
          html {
            -webkit-overflow-scrolling: touch;
          }

          /* Optimize backdrop-blur for older browsers */
          .backdrop-blur-sm {
            -webkit-backdrop-filter: blur(4px);
            backdrop-filter: blur(4px);
          }

          @supports not (backdrop-filter: blur(4px)) {
            .backdrop-blur-sm {
              background-color: rgba(255, 255, 255, 0.9);
            }
          }
        `}</style>
        
      </div>
    </Router>
  );
};

export default App;